import React from "react";
import styled from "styled-components/macro";
import ReactGA from "react-ga";
import { rem } from "polished";
import { Link, useHistory, useLocation } from "react-router-dom";

import Info from "../../../components/Info";
import McvBar from "../../../components/McvBar";
import InfoTip from "../../../components/InfoTip";
import ErrorMessage from "../../../components/ErrorMessage";
import Supports from "../components/Supports";
import SideBar from "../../../components/SideBar";
import FormLabel from "../../../components/FormLabel";
import PostcodeValidator from "../../../components/PostcodeValidator";
import Checkbox from "../../../components/Checkbox";

import renderCmsCopy from "../../../helpers/renderCmsCopy";
import { ReactComponent as ImmediateDangerIcon } from "../../../assets/icons/ImmediateDanger.svg";
import Button from "../../../components/Button";
import media from "../../../theme/media";
import colors from "../../../theme/colors";
import QuestionSpacer from "../../../components/QuestionSpacer";

const HomePage = () => {
  const { pathname } = useLocation();
  const { push } = useHistory();

  const [error, setError] = React.useState<boolean>(false);
  const [postcodeStatus, setPostcodeStatus] = React.useState<string>("");
  const [userHasAgreed, setUserHasAgreed] = React.useState(false);
  const [userApplyingAgainstFamilyMember, setUserApplyingAgainstFamilyMember] =
    React.useState(false);

  React.useEffect(() => {
    ReactGA.pageview(pathname);
  }, [pathname]);

  return (
    <Wrapper>
      <McvBar />
      <Intro>
        <IntroInner>
          <div>
            <h1>Apply for a Family Violence Intervention Order (FVIO)</h1>
            <IntroInfoWrapper>
              <p>
                <ImmediateDangerIcon />
                &nbsp;{" "}
                <strong>
                  If you are in immediate danger, take action by calling 000.
                </strong>
              </p>
              <div>
                Or if you are safely able to do so, contact your nearest police
                station or{" "}
                <Info
                  displayText="court location"
                  headingText="nearest court"
                  bodyText='<a href="https://www.mcv.vic.gov.au/going-court/find-court" title="_blank" rel="noopener noreferrer" >You can find your nearest court location here.</a>'
                  inherit
                />{" "}
                . Please note that the courts are open 9am-4.30pm Mon-Fri,
                excluding public holidays.
              </div>
            </IntroInfoWrapper>
            Fill out this online form to apply for an FVIO if:
            <ul>
              <li>
                {renderCmsCopy(
                  `you are applying ||against a family member, |Family members or partners|For family violence intervention orders, family members are: <br><br>•  people who share an intimate personal relationship, whether there is a sexual relationship or not—for example, married, de facto or domestic partners <br>•  parents and children, including children of an intimate partner<br>•  relatives by birth, marriage or adoption <br>•  people you treat like a family member—for example, a carer, guardian, or person who is related to you within the family structure of your culture.<br><br>They also include anyone who was a family member in the past.||`
                )}{" "}
                {renderCmsCopy(
                  `||partner or ex-partner.|Family members or partners|For family violence intervention orders, family members are: <br><br>•  people who share an intimate personal relationship, whether there is a sexual relationship or not—for example, married, de facto or domestic partners <br>•  parents and children, including children of an intimate partner<br>•  relatives by birth, marriage or adoption <br>•  people you treat like a family member—for example, a carer, guardian, or person who is related to you within the family structure of your culture.<br><br>They also include anyone who was a family member in the past.||`
                )}
              </li>
              <li>you are 18 years of age or older.</li>
            </ul>
            <div>
              <p>
                You can use this form to apply for an intervention order for you
                and your children under 18 years of age.
              </p>
              <p>
                This form cannot be a filled out by someone else on your behalf.
              </p>
              <p>
                If you are under 18 years of age, you will need to contact the{" "}
                <a
                  href="https://www.childrenscourt.vic.gov.au/contact-us"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Children's Court of Victoria
                </a>{" "}
                to complete an application
              </p>
              <div>
                {renderCmsCopy(
                  `If you need to extend, vary or revoke an existing Intervention Order, you will need to contact your ||local court|your local court| <a href="https://www.mcv.vic.gov.au/going-court/find-court" title="_blank" rel="noopener noreferrer" >You can find your nearest court location here.</a>||`
                )}
              </div>
              <QuestionSpacer size={120} />
            </div>
          </div>
          <SideBar />
        </IntroInner>
      </Intro>
      <Start>
        <StartInner>
          <div style={{ maxWidth: "647px" }}>
            <h2>Start your online FVIO application here</h2>

            <div>
              <InfoTip>
                Have an account? Log in to start or continue an
                application.&nbsp;{" "}
                <div>
                  <Link to="/login">Log in</Link>
                </div>
              </InfoTip>
              <QuestionSpacer size={10} />
            </div>
            <FormLabel text="Enter the postcode of your current residential address" />
            <StyledInfo string="Why are we asking this?|Why are we asking this?|We need your current residential address to identify which court will process your application. A residential address can be the address of your house, refuge, crisis accommodation or friend’s house." />
            <br />
            <br />

            <div>
              <PostcodeValidator setPostcodeStatus={setPostcodeStatus} />
            </div>
          </div>
          <div style={{ maxWidth: "647px" }}>
            {postcodeStatus === "ok" && (
              <>
                <QuestionSpacer size={25} />
                <Checkbox
                  label="I am at least 18 years of age"
                  name="agree"
                  value="yes"
                  onCheckboxChange={(e) => setUserHasAgreed(e.target.checked)}
                  checked={userHasAgreed}
                />
                <Checkbox
                  label={
                    "I am applying against a family member, partner or ex-partner"
                  }
                  name="against family member"
                  value="yes"
                  onCheckboxChange={(e) =>
                    setUserApplyingAgainstFamilyMember(e.target.checked)
                  }
                  checked={userApplyingAgainstFamilyMember}
                />
              </>
            )}
          </div>
          {error && (
            <div>
              <ErrorMessage>
                Please ensure you have entered a valid postcode and agreed to
                the terms
              </ErrorMessage>
            </div>
          )}
          <QuestionSpacer size={25} />
          <Button
            action={() => {
              if (!userHasAgreed || !userApplyingAgainstFamilyMember) {
                setError(true);
              } else {
                push("/application");
              }
            }}
          >
            Begin application
          </Button>
          <br />
          <LowerInfo>
            <SideBar />
          </LowerInfo>
        </StartInner>
      </Start>
      <Supports />
    </Wrapper>
  );
};

export default HomePage;

const leftPaddingDesktop = "120px";
const leftPaddingTablet = "15px";

const Wrapper = styled.section`
  position: relative;
`;

const Intro = styled.section`
  background: #d4e1ed;
  color: #3c3c3c;
  padding: 60px 15px 30px;
`;

const IntroInner = styled.div`
  width: 100%;
  max-width: 1310px;
  position: relative;
  margin: 0 auto;
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: flex-start;

  font-size: ${rem(18)};
  line-height: 1.44;
  strong {
    font-weight: bold;
  }

  a {
    color: ${colors.blue};
  }

  small {
    font-size: 90%;
  }

  > :first-child {
    width: 680px;
    ${media.greaterThan("tablet")`
      padding-right: 20px;
    `}
  }

  > :last-child {
    display: none;
    ${media.greaterThan("tablet")`
      display: block;
    `}
  }

  ${media.greaterThan("tablet")`
    padding-left: ${leftPaddingTablet};
  `}
  ${media.greaterThan("desktop")`
    padding-left: ${leftPaddingDesktop};
  `}

  ul {
    margin: 18px 0 36px;
  }

  li {
    margin-bottom: 0.75em;
  }
`;

const Start = styled.div`
  padding: 0 15px 70px;
  padding-bottom: 0px;
  width: 100%;
  max-width: 1310px;
  position: relative;
  display: flex;
  flex-direction: row;
  justify-content: center;
  margin: 0 auto;

  ${media.greaterThan("desktop")`
     padding-left: ${leftPaddingTablet};
     justify-content: flex-start;
    `}
`;

const StartInner = styled.div`
  background: white;
  position: relative;
  width: 100%;
  max-width: 1310px;
  padding: 60px 0;
  min-height: ${rem(350)};
  z-index: 99;
  ${media.greaterThan("tablet")`
    padding-left: ${leftPaddingTablet};
  transform: translateY(-93px);
  box-shadow: 0px 2px 4px rgba(0, 0, 0, 0.13);

  h2 {
    margin-bottom: 0.75em;
  }


  `};
  ${media.greaterThan("desktop")`
    padding-left: 105px;
  
  `};
`;

const StyledInfo = styled(Info)`
  margin-top: -1em;
`;

const LowerInfo = styled.div`
  margin-top: 60px;
  display: block;
  ${media.greaterThan("tablet")`
    display: none;
  `}
`;

const IntroInfoWrapper = styled.div`
  background: #fff;
  width: 100%;
  padding: 15px 20px 15px 37px;
  position: relative;
  margin-bottom: 21px;

  ${media.greaterThan("tablet")`
    padding: 16px 18px 16px;
  `}

  &:first-child {
    margin-top: 0;
  }

  &:last-child {
    margin-bottom: 0;
  }

  line-height: 1.44;

  svg {
    width: 17px;
    height: 16px;
    display: inline-block;
    margin-right: 2px;
    position: absolute;
    left: 10px;
    top: 16px;

    ${media.greaterThan("tablet")`
      position: static;
    `}
  }

  strong {
    font-size: ${rem(18)};
    font-weight: bold;

    br {
      line-height: 0.8em;
    }
  }

  p:first-child {
    padding-top: 0;
    margin-top: 0;
    margin-bottom: 0.3em;
  }
`;
